import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/axios";

function AdminUserDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  const [pwUpdating, setPwUpdating] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [userRes, subRes] = await Promise.all([
          api.get(`/admin/user-detail/${id}`),
          api.get(`/admin/user-subscription/${id}`),
        ]);
        setUser(userRes.data);
        setSubscription(subRes.data);
      } catch (err) {
        console.error("Failed to load user");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleAutoReset = async () => {
    const confirmMsg = `System will generate a random secure password for ${user.name} and email it to ${user.email}. Proceed?`;
    if (!window.confirm(confirmMsg)) return;

    setPwUpdating(true);
    try {
      const res = await api.put(`/admin/auto-reset-password/${id}`);
      alert(res.data.message);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to generate password.");
    } finally {
      setPwUpdating(false);
    }
  };

  if (loading)
    return (
      <div
        style={{
          padding: "48px",
          textAlign: "center",
          fontWeight: 700,
          color: "#1D5DA8",
          fontFamily: "Inter, sans-serif",
        }}
      >
        Loading User Profile...
      </div>
    );

  if (!user)
    return (
      <div
        style={{
          padding: "48px",
          textAlign: "center",
          color: "#dc3545",
          fontWeight: 700,
          fontFamily: "Inter, sans-serif",
        }}
      >
        User not found.
      </div>
    );

  const profileRows = [
    { label: "ID", value: user.id },
    { label: "COMMON UUID", value: user.common_id },
    { label: "PROVIDER", value: user.provider || "local" },
    { label: "ACCOUNT TYPE", value: user.is_admin ? "Admin" : "Staff" },
    { label: "HAS WORKSPACE", value: user.has_workspace ? "Yes" : "No" },
    { label: "STATUS", value: user.is_active ? "Active" : "Inactive" },
    { label: "ASSIGNED ROLE", value: user.assigned_role || "None" },
    { label: "ROLE ID", value: user.role_id || "N/A" },
    { label: "INVITED BY", value: user.invited_by || "N/A (Direct Signup)" },
    { label: "JOINED", value: new Date(user.created_at).toLocaleString() },
  ];

  const subscriptionRows = subscription
    ? [
        { label: "User ID", value: subscription.user_id },
        { label: "Subscription ID", value: subscription.id },
        { label: "Plan ID", value: subscription.plan_id },
        {
          label: "Price",
          value: subscription.price ? `$${subscription.price}/month` : "N/A",
          strong: true,
        },
        {
          label: "Status",
          value: subscription.status,
          status: true,
        },
        {
          label: "Start Date",
          value: subscription.start_date
            ? new Date(subscription.start_date).toLocaleDateString()
            : "N/A",
        },
        {
          label: "Next Renewal",
          value: subscription.next_renewal
            ? new Date(subscription.next_renewal).toLocaleDateString()
            : "N/A",
        },
      ]
    : [];

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap"
        rel="stylesheet"
      />

      <style>{`
        .audp-root * {
          box-sizing: border-box;
          font-family: 'Inter', sans-serif;
        }

        .audp-card {
          background: #ffffff;
          border: 1px solid #e4e8f1;
          border-radius: 12px;
          box-shadow: 0 1px 2px rgba(15, 27, 53, 0.04);
        }

        .audp-detail-row {
          display: grid;
          grid-template-columns: 190px 1fr;
          align-items: start;
          gap: 14px;
          padding: 12px 0;
          border-bottom: 1px solid #edf1f6;
        }

        .audp-detail-row:last-child {
          border-bottom: none;
        }

        .audp-detail-label {
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 1.1px;
          color: #3f4b63;
          text-transform: uppercase;
        }

        .audp-detail-value {
          font-size: 15px;
          color: #495468;
          line-height: 1.45;
          word-break: break-word;
        }

        .audp-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 4px 10px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 700;
          line-height: 1;
        }

        .audp-back-link {
          border: none;
          background: transparent;
          padding: 0;
          margin: 0;
          color: #1f2b45;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
        }

        .audp-back-link:hover {
          opacity: 0.82;
        }

        .audp-sub-card {
          background: #f3f5f9;
          border: none;
          border-radius: 8px;
          box-shadow: none;
          padding: 26px 28px 24px;
          min-height: 468px;
        }

        .audp-sub-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 22px;
        }

        .audp-sub-title {
          font-size: 18px;
          font-weight: 800;
          color: #1d2433;
          letter-spacing: -0.2px;
        }

        .audp-plan-box {
          background: #ffffff;
          border-radius: 4px;
          border: 1px solid #edf1f5;
          padding: 18px 18px 16px;
          margin-bottom: 18px;
        }

        .audp-plan-label {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 1.6px;
          text-transform: uppercase;
          color: #8f98a7;
          margin-bottom: 10px;
        }

        .audp-plan-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        .audp-plan-name {
          font-size: 14px;
          font-weight: 800;
          color: #0d2b8f;
          line-height: 1.3;
          letter-spacing: -0.1px;
        }

        .audp-tier-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 40px;
          height: 22px;
          padding: 0 8px;
          border-radius: 2px;
          background: #e8c8b8;
          color: #8b5c46;
          font-size: 10px;
          font-weight: 800;
          text-transform: uppercase;
          line-height: 1;
          white-space: nowrap;
        }

        .audp-sub-rows {
          margin-bottom: 18px;
        }

        .audp-sub-row {
          display: grid;
          grid-template-columns: 110px 1fr;
          align-items: center;
          gap: 14px;
          padding: 10px 0;
        }

        .audp-sub-row-label {
          font-size: 12px;
          font-weight: 500;
          color: #58708f;
        }

        .audp-sub-row-value {
          font-size: 14px;
          font-weight: 500;
          color: #232c3b;
          text-align: right;
          line-height: 1.35;
        }

        .audp-sub-row-value.strong {
          font-size: 16px;
          font-weight: 800;
          color: #202633;
        }

        .audp-sub-row-value.active {
          color: #19a44b;
          font-weight: 700;
        }

        .audp-manage-btn {
          width: 100%;
          height: 34px;
          border: none;
          border-radius: 2px;
          background: #e6e9ee;
          color: #0d2b8f;
          font-size: 12px;
          font-weight: 800;
          cursor: pointer;
        }

        .audp-manage-btn:hover {
          background: #dde2e8;
        }

        .audp-security-card {
          background: #ffffff;
          border: 1px solid #efcfcf;
          border-top: 4px solid #df9e9e;
          border-radius: 8px;
          box-shadow: none;
          padding: 22px 22px 22px;
          min-height: 318px;
        }

        .audp-security-icon-wrap {
          display: flex;
          justify-content: center;
          margin-bottom: 16px;
        }

        .audp-security-icon-box {
          width: 42px;
          height: 42px;
          border-radius: 12px;
          background: #f7dada;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #b71d1d;
          font-size: 20px;
        }

        .audp-security-title {
          text-align: center;
          font-size: 17px;
          font-weight: 800;
          color: #262b35;
          line-height: 1.35;
          margin-bottom: 14px;
          letter-spacing: -0.2px;
        }

        .audp-security-desc {
          text-align: center;
          font-size: 13px;
          color: #5f6876;
          line-height: 1.8;
          margin: 0 0 18px 0;
          max-width: 250px;
          margin-left: auto;
          margin-right: auto;
        }

        .audp-security-btn {
          border: none;
          background: #0d2b8f;
          color: #ffffff;
          border-radius: 4px;
          padding: 11px 14px;
          font-size: 13px;
          font-weight: 800;
          line-height: 1.2;
          cursor: pointer;
          width: 100%;
          white-space: nowrap;
          box-shadow: none;
        }

        .audp-security-btn:hover {
          background: #0b2477;
        }

        .audp-security-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        @media (max-width: 1200px) {
          .audp-main-grid {
            grid-template-columns: 1fr;
          }

          .audp-security-col {
            max-width: 420px;
          }

          .audp-sub-card,
          .audp-security-card {
            min-height: auto;
          }
        }

        @media (max-width: 768px) {
          .audp-page {
            padding: 24px 18px !important;
          }

          .audp-detail-row {
            grid-template-columns: 1fr;
            gap: 6px;
          }

          .audp-topline {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 10px !important;
          }

          .audp-sub-row {
            grid-template-columns: 1fr;
            gap: 4px;
          }

          .audp-sub-row-value {
            text-align: left;
          }
        }
      `}</style>

      <div
        className="audp-root audp-page"
        style={{ 
          padding: "34px 28px 40px", 
          background: "#f6f7fb", 
          height: "100vh",       // Changed from minHeight
          overflowY: "auto"      // ADDED THIS LINE
        }}
      >
        <div style={{ marginBottom: 18 }}>
          <div
            className="audp-topline"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                fontSize: 13,
                fontWeight: 800,
                letterSpacing: "1.3px",
                color: "#0d2b8f",
                textTransform: "uppercase",
              }}
            >
              KINEXAR INTERNAL
            </div>

            <div style={{ color: "#bcc5d3", fontSize: 13 }}>/</div>

            <button className="audp-back-link" onClick={() => navigate(-1)}>
              ← Back to User Infrastructure
            </button>
          </div>

          <h1
            style={{
              margin: "0 0 10px 0",
              fontSize: 24,
              fontWeight: 800,
              color: "#111827",
              letterSpacing: "-0.5px",
            }}
          >
            User Deep Dive
          </h1>
        </div>

        <div
          className="audp-main-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "1.35fr 0.93fr 0.78fr",
            gap: 14,
            alignItems: "start",
          }}
        >
          {/* left profile card */}
          <div className="audp-card" style={{ padding: 26 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "84px 1fr auto",
                gap: 18,
                alignItems: "start",
                marginBottom: 26,
              }}
            >
              <div
                style={{
                  width: 84,
                  height: 84,
                  borderRadius: 16,
                  background: "#2b67b1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: 38,
                  fontWeight: 700,
                }}
              >
                {user.name?.charAt(0).toUpperCase()}
              </div>

              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 800,
                    color: "#172033",
                    marginBottom: 3,
                    lineHeight: 1.2,
                    wordBreak: "break-word",
                  }}
                >
                  {user.name}
                </div>

                <div
                  style={{
                    fontSize: 15,
                    color: "#6f7d95",
                    marginBottom: 10,
                    wordBreak: "break-word",
                  }}
                >
                  {user.email}
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <span
                    className="audp-badge"
                    style={{
                      background: "#e6efff",
                      color: "#0d61d9",
                    }}
                  >
                    {user.is_admin ? "ADMIN" : "STAFF"}
                  </span>

                  <span
                    className="audp-badge"
                    style={{
                      background: user.is_active ? "#e7f8ec" : "#fdeaea",
                      color: user.is_active ? "#22a559" : "#dc3545",
                      gap: 6,
                    }}
                  >
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: user.is_active ? "#22a559" : "#dc3545",
                        display: "inline-block",
                      }}
                    />
                    {user.is_active ? "ACTIVE" : "INACTIVE"}
                  </span>
                </div>
              </div>

              <button
                type="button"
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#0d2b8f",
                  fontSize: 22,
                  lineHeight: 1,
                  padding: 0,
                  cursor: "default",
                }}
              >
                
              </button>
            </div>

            <div>
              {profileRows.map(({ label, value }) => (
                <div key={label} className="audp-detail-row">
                  <div className="audp-detail-label">{label}</div>
                  <div className="audp-detail-value">{value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* subscription card */}
          <div className="audp-sub-card">
            <div className="audp-sub-header">
              <span style={{ fontSize: 20, color: "#0d2b8f", lineHeight: 1 }}>▣</span>
              <div className="audp-sub-title">Subscription Details</div>
            </div>

            {!subscription ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#7e8797",
                  fontSize: 14,
                  padding: "52px 0",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: 34, marginBottom: 10 }}>📭</div>
                <div>No active subscription for this user.</div>
              </div>
            ) : (
              <>
                <div className="audp-plan-box">
                  <div className="audp-plan-label">PLAN NAME</div>

                  <div className="audp-plan-row">
                    <div className="audp-plan-name">
                      {subscription.plan_name || "N/A"}
                    </div>

                    <span className="audp-tier-badge">TIER 3</span>
                  </div>
                </div>

                <div className="audp-sub-rows">
                  {subscriptionRows.map(({ label, value, strong, status }) => (
                    <div key={label} className="audp-sub-row">
                      <div className="audp-sub-row-label">{label}</div>

                      <div
                        className={`audp-sub-row-value ${strong ? "strong" : ""} ${
                          status && value === "Active" ? "active" : ""
                        }`}
                      >
                        {value}
                      </div>
                    </div>
                  ))}
                </div>

                {/* <button type="button" className="audp-manage-btn">
                  Manage Billing & Usage
                </button> */}
              </>
            )}
          </div>

          {/* security override */}
          <div className="audp-security-col">
            <div className="audp-security-card">
              <div className="audp-security-icon-wrap">
                <div className="audp-security-icon-box">⏎</div>
              </div>

              <div className="audp-security-title">
                Administrative Security Override
              </div>

              <p className="audp-security-desc">
                Clicking the button below will generate a random high-strength
                password and automatically email it to the user. This action is
                logged for audit compliance.
              </p>

              <button
                className="audp-security-btn"
                onClick={handleAutoReset}
                disabled={pwUpdating}
              >
                {pwUpdating ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      style={{ verticalAlign: "middle" }}
                    ></span>
                    Generating & Emailing...
                  </>
                ) : (
                  "✉ Generate & Email Updated Password"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AdminUserDetailPage;